import styled from 'styled-components';
import { LoadingSpinner } from 'styles/sharedStyles';

export const Container = styled.div`
  padding: 20px 20px 10px 20px;

  * :not(a) {
    color: black;
  }
`;

export const TitleContainer = styled.div`
  position: relative;

  display: flex;
  justify-content: end;
  gap: 70px;

  margin-bottom: 20px;
`;

export const CloseButton = styled.div`
  cursor: pointer;
`;

export const Title = styled.div`
  font-weight: bold;
  text-align: center;
  letter-spacing: 0.01px; // fixing the letter spacing in generated PDF
  font-family: Arial, Helvetica, 'Times New Roman', serif; // fixing the custom font (Roboto) caused issues in generated PDF
`;

export const Disclaimer = styled.p`
  color: ${({ theme }) => theme.palette.grey44};
`;

export const LinkTagContainer = styled.p`
  text-align: right;
`;

export const LinkTag = styled.div`
  color: ${({ theme }) => theme.palette.primary};
  text-decoration: underline;
  cursor: pointer;
`;

export const LegalTextContainer = styled.div`
  padding: 20px;
  max-height: 65vh;
  overflow: auto;
  text-align: justify;
  text-transform: none; // this to prevent the text from being all uppercase which is not handled well by jsPDF

  // makes the form separated from modal, with a shadow
  > div {
    border-radius: 5px;
    padding: 20px;
    box-shadow: 0px 0px 24px 2px rgba(0, 0, 0, 0.18);
  }

  p > u {
    font-weight: bold;
    letter-spacing: 0.03em;
    font-family: Arial, Helvetica, 'Times New Roman', serif; // fixing the custom font (Roboto) caused issues in generated PDF
    line-height: 1.5;
  }

  strong,
  * > strong {
    letter-spacing: 0.03em; // fixing the letter spacing in generated PDF
    font-family: Arial, Helvetica, 'Times New Roman', serif; // fixing the custom font (Roboto) caused issues in generated PDF
  }
`;

export const LegalText = styled.p<{
  textAlign?: 'left' | 'right' | 'center' | 'justify' | 'initial' | 'inherit';
}>`
  text-align: ${({ textAlign }) => textAlign || 'left'};
`;

export const TacContainer = styled.div`
  margin-bottom: 10px;
`;

export const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding-bottom: 20px;
`;

export const FormItem = styled.p`
  padding-top: 1px; /* Allows for hr margin to start at top of this component */
  line-height: 1.5;

  &::after {
    content: '';
    display: table;
    clear: both;
  }

  span {
    margin-right: 5px;
    float: left;
  }

  hr {
    border: 1px solid black;
    border-width: 1px 0 0 0;
    margin-top: 18px;
  }
`;

export const Separator = styled.div`
  width: 100%;
  border-top: 1px solid black;
`;

export const FormGrid = styled.div<{
  cellPerRow: number;
  noMarginBottom?: boolean;
  autoFirstCellSize?: boolean;
}>`
  display: grid;
  grid-template-columns: ${({ cellPerRow, autoFirstCellSize }) => {
    if (autoFirstCellSize) {
      return `auto 1fr;`;
    }
    if (cellPerRow) {
      return `repeat(${cellPerRow}, 1fr);`;
    }
  }};

  grid-column-gap: 0;
  grid-row-gap: 5px;

  margin-bottom: ${({ noMarginBottom }) => (noMarginBottom ? '0' : '20px')};

  > * {
    border-bottom: 1px solid black;
  }

  > *:nth-last-child(-n + ${({ cellPerRow }) => cellPerRow}) {
    border-bottom: none;
  }
`;

export const FormGridItem = styled.div<{
  noBorder?: boolean;
  padding?: string;
}>`
  border-bottom: ${({ noBorder }) => (noBorder ? 'none' : '1px solid black')};
  padding: ${({ padding }) => padding || '0 5px'};

  font-family: Arial, Helvetica, 'Times New Roman', serif; // fixing the custom font (Roboto) caused issues in generated PDF
`;

export const FormCheckboxContainer = styled.div`
  display: flex;
  margin: 20px 0;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
`;

export const Th = styled.th`
  border: 1px solid #ddd;
  padding: 8px;
  background-color: #f2f2f2;
  text-align: left;

  letter-spacing: 0.03em;
  font-family: Arial, Helvetica, 'Times New Roman', serif; // fixing the custom font (Roboto) caused issues in generated PDF
`;

export const Td = styled.td<{ $width?: string }>`
  border: 1px solid #ddd;
  padding: 8px;
  width: ${({ $width }) => $width && $width};
  text-align: left;
`;

export const TotalTr = styled.tr`
  > td {
    font-weight: bold;
    letter-spacing: 0.03em; // fixing the letter spacing in generated PDF
    font-family: Arial, Helvetica, 'Times New Roman', serif; // fixing the custom font (Roboto) caused issues in generated PDF
  }
`;

export const ServicePlanTermWrapper = styled.div`
  font-size: 0.9rem;
  margin: 0px 10px;
  line-height: 1.2rem;
`;

export const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 200;
  animation: fadeIn 0.4s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const PDFLoadingSpinner = styled(LoadingSpinner)`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #000;
`;

export const ServicePlanSubText = styled(ServicePlanTermWrapper)`
  margin: 0;
  font-style: italic;
`;

export const HelperText = styled.span`
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  display: inline-block;
`;
